import {
	DEFAULT_ASSET_TYPES,
	IkeFieldType,
	isColorEditableDefaultAssetType,
	isDefaultAssetTypeTitle,
	isVisibilityEditableDefaultAssetType,
	rotationForDefaultAssetType,
} from '@ikegps/core-api';
import { FormModelField, type FormModelFieldState, FormModelMutatorField } from './form.field';

/**
 * Titles are compared case-insensitively everywhere identity matters (index keying, lookup, duplicate
 * detection) — a collected value or an API/Excel-imported title only has to match a schema title's
 * spelling, not its exact casing.
 */
function normalizeAssetTypeTitle(title: string): string {
	return title.trim().toLowerCase();
}

export interface FormModelFieldAssetTypeOption {
	/** Material Icons font ligature (e.g. `light_mode`). */
	icon: string;
	/** Display title — editable; the stable identity a collected asset type reference stores. Unique + non-empty within a field. */
	title: string;
	/** Color for this asset type (HTML hex). */
	color: string;
	visible: boolean;
}

export interface FormModelFieldAssetTypeState extends FormModelFieldState {
	assetTypes: FormModelFieldAssetTypeOption[];
}

export class FormModelMutatorAssetType extends FormModelMutatorField<FormModelFieldAssetType> {
	private getAssetTypes(): FormModelFieldAssetTypeOption[] {
		const assetTypes = this.field.stateValue.assetTypes;
		if (Array.isArray(assetTypes)) {
			return [...assetTypes];
		}
		return FormModelFieldAssetType.defaultOptions();
	}

	private write(assetTypes: FormModelFieldAssetTypeOption[]) {
		this.state.setFieldValue(this.field.fieldId, { ...this.field.stateValue, assetTypes });
	}

	/** Add a new asset type, or replace the one at `index`. */
	option(option: FormModelFieldAssetTypeOption, index = -1) {
		const assetTypes = this.getAssetTypes();
		if (index === -1 || index > assetTypes.length - 1) {
			assetTypes.push(option);
		} else {
			assetTypes[index] = option;
		}
		this.write(assetTypes);
	}

	options(assetTypes: FormModelFieldAssetTypeOption[]) {
		this.write(assetTypes);
	}

	remove(index: number) {
		const assetTypes = this.getAssetTypes();
		if (index < 0 || index >= assetTypes.length) {
			throw new Error('Index is out of bounds ' + index);
		}
		assetTypes.splice(index, 1);
		this.write(assetTypes);
	}

	move(index: number, delta: number) {
		const assetTypes = this.getAssetTypes();
		const target = index + delta;
		if (index < 0 || index >= assetTypes.length || target < 0 || target >= assetTypes.length) {
			return;
		}
		const [moved] = assetTypes.splice(index, 1);
		assetTypes.splice(target, 0, moved);
		this.write(assetTypes);
	}

	setTitle(index: number, title: string) {
		this.update(index, (option) => ({ ...option, title }));
	}

	setIcon(index: number, icon: string, color: string) {
		this.update(index, (option) => ({ ...option, icon, color }));
	}

	/** Color-only update, used for preset rows: their icon is fixed, but the color remains editable. */
	setColor(index: number, color: string) {
		this.update(index, (option) => ({ ...option, color }));
	}

	setVisibility(index: number, visible: boolean) {
		this.update(index, (option) => ({ ...option, visible }));
	}

	toggleVisibility(index: number) {
		this.update(index, (option) => ({ ...option, visible: !option.visible }));
	}

	private update(index: number, change: (option: FormModelFieldAssetTypeOption) => FormModelFieldAssetTypeOption) {
		const assetTypes = this.getAssetTypes();
		if (assetTypes[index] == null) {
			return;
		}
		assetTypes[index] = change(assetTypes[index]);
		this.write(assetTypes);
	}
}

export class FormModelFieldAssetType extends FormModelField<FormModelFieldAssetTypeState> {
	static isAssetTypeField(field: FormModelField): field is FormModelFieldAssetType {
		return field.type === IkeFieldType.ASSET_TYPE;
	}

	/**
	 * The default asset-type library seeded on every new asset type field (all visible). Presets are
	 * non-editable (visibility-toggle-only); a row is a preset iff its title is in this set — no stored
	 * flag (see `isDefaultAssetTypeTitle`).
	 */
	static defaultOptions(): FormModelFieldAssetTypeOption[] {
		return DEFAULT_ASSET_TYPES.map((type) => ({
			icon: type.icon,
			color: type.color,
			title: type.title,
			visible: true,
		}));
	}

	private _AssetTypeMutator: FormModelMutatorAssetType | null = null;
	override get Mutator(): FormModelMutatorAssetType {
		if (this._AssetTypeMutator == null) {
			this._AssetTypeMutator = new FormModelMutatorAssetType(this);
		}
		return this._AssetTypeMutator;
	}

	get assetTypes(): FormModelFieldAssetTypeOption[] {
		return this.stateValue.assetTypes || FormModelFieldAssetType.defaultOptions();
	}

	get visibleAssetTypes(): FormModelFieldAssetTypeOption[] {
		return this.assetTypes.filter((assetType) => assetType.visible);
	}

	private _assetTypeIndex: Map<string, FormModelFieldAssetTypeOption> | null = null;
	private _assetTypeIndexSource: FormModelFieldAssetTypeOption[] | null = null;

	/**
	 * Title→option lookup cached against the assetTypes array identity. Mutations always replace the array
	 * (mutator clones + writes a new one), so an identity miss rebuilds the map. Keyed by normalized title
	 * (see `normalizeAssetTypeTitle`) so lookup matches `hasDuplicateTitle`'s case-insensitive policy.
	 */
	private assetTypeIndex(): Map<string, FormModelFieldAssetTypeOption> {
		const assetTypes = this.assetTypes;
		if (this._assetTypeIndex == null || this._assetTypeIndexSource !== assetTypes) {
			const index = new Map<string, FormModelFieldAssetTypeOption>();
			for (const assetType of assetTypes) {
				const key = normalizeAssetTypeTitle(assetType.title);
				if (!index.has(key)) {
					index.set(key, assetType);
				}
			}
			this._assetTypeIndex = index;
			this._assetTypeIndexSource = assetTypes;
		}
		return this._assetTypeIndex;
	}

	findAssetType(title: string | null): FormModelFieldAssetTypeOption | null {
		if (title == null || title === '') {
			return null;
		}
		return this.assetTypeIndex().get(normalizeAssetTypeTitle(title)) ?? null;
	}

	/**
	 * Whether `title` (the row at `index` is excluded) collides case-insensitively with another row's
	 * title. Shared by `isValid` below and by the asset-type editor's per-row duplicate error, so the
	 * designer UI and the model agree on exactly one casing policy.
	 */
	hasDuplicateTitle(index: number, title: string): boolean {
		const target = normalizeAssetTypeTitle(title);
		if (target === '') {
			return false;
		}
		return this.assetTypes.some((other, i) => i !== index && normalizeAssetTypeTitle(other.title) === target);
	}

	/**
	 * Row is a fixed preset only if it's the FIRST occurrence of a default-library title. A later row
	 * duplicating a preset title is a custom row kept editable/removable, NOT a preset.
	 */
	isPresetOption(index: number): boolean {
		const option = this.assetTypes[index];
		if (option == null || !isDefaultAssetTypeTitle(option.title)) {
			return false;
		}
		return this.assetTypes.findIndex((other) => other.title === option.title) === index;
	}

	/** Whether this row's color may be edited. Always true for a custom row; a preset may lock it (e.g. "Collection"). */
	isColorEditable(index: number): boolean {
		if (!this.isPresetOption(index)) {
			return true;
		}
		return isColorEditableDefaultAssetType(this.assetTypes[index].title);
	}

	/** Whether this row's visibility may be toggled. Always true for a custom row; a preset may lock it (e.g. "Collection"). */
	isVisibilityEditable(index: number): boolean {
		if (!this.isPresetOption(index)) {
			return true;
		}
		return isVisibilityEditableDefaultAssetType(this.assetTypes[index].title);
	}

	/**
	 * Degrees clockwise to rotate this row's icon glyph (e.g. Valve's hourglass, turned on its side).
	 * Derived from the preset library by title, like `isColorEditable` — not stored on the row itself.
	 */
	iconRotation(index: number): number {
		if (!this.isPresetOption(index)) {
			return 0;
		}
		return rotationForDefaultAssetType(this.assetTypes[index].title);
	}

	get isValid(): boolean {
		const titles = this.assetTypes.map((assetType) => assetType.title.trim());
		const hasVisibleTitled = this.assetTypes.some(
			(assetType) => assetType.visible && assetType.title.trim().length > 0
		);
		const hasEmpty = titles.some((title) => title.length === 0);
		// Non-empty titles only: hasEmpty already fails validation on its own, and normalizing would
		// otherwise also flag two blank titles as "duplicates" of each other.
		const normalizedTitles = titles.filter((title) => title.length > 0).map((title) => title.toLowerCase());
		const hasDuplicate = new Set(normalizedTitles).size !== normalizedTitles.length;
		const hasMissingIcon = this.assetTypes.some((assetType) => assetType.icon.trim().length === 0);
		return hasVisibleTitled && !hasEmpty && !hasDuplicate && !hasMissingIcon;
	}
}
